/**
 * Core domain types for 1-T.
 *
 * Everything is a pure data description so we can later layer on real stats,
 * chemistry pairs, multiple eras of the same person, seeded runs and
 * leaderboards without restructuring anything.
 */

export const ROLES = ['CEO', 'CTO', 'CPO', 'COO', 'GROWTH', 'WILDCARD'] as const;
export type Role = (typeof ROLES)[number];

/** Title-case labels for share cards and prose. */
export const ROLE_LABELS: Record<Role, string> = {
  CEO: 'CEO',
  CTO: 'CTO',
  CPO: 'CPO',
  COO: 'COO',
  GROWTH: 'Growth',
  WILDCARD: 'Wildcard',
};

/** The five pillars a company is judged on, plus chaos (lower is calmer). */
export const PILLARS = [
  'product',
  'engineering',
  'growth',
  'execution',
  'fundraising',
  'chaos',
] as const;
export type Pillar = (typeof PILLARS)[number];

/** The five pillars that are unambiguously good to have. */
export const CORE_PILLARS = [
  'product',
  'engineering',
  'growth',
  'execution',
  'fundraising',
] as const;
export type CorePillar = (typeof CORE_PILLARS)[number];

export type Stats = Record<Pillar, number>;

export type Person = {
  id: PersonId;
  name: string;
  /** Monogram shown on the avatar. Derived from the name unless overridden. */
  initials: string;
  /** One-liner shown in the cohort list. */
  tagline: string;
  /** Short chips: `Product visionary`, `Elite operator`, `High chaos`, ... */
  tags: string[];
  /** 0-100 per pillar. */
  stats: Stats;
  /**
   * Multiplier applied when this person is drafted into a given role.
   * Missing roles fall back to `DEFAULT_AFFINITY`.
   */
  affinity: Partial<Record<Role, number>>;
  /**
   * Optional era label, so later we can ship "Elon Musk — 2008" and
   * "Elon Musk — 2022" as distinct entries.
   */
  era?: string;
};

export type PersonId = string;

/**
 * How famous / how ridiculous a draw is. Purely descriptive today — every
 * cohort is still drawn with equal probability — but it's the hook for
 * weighted draws or tier-based modes later.
 */
export const COHORT_TIERS = ['A', 'B', 'C', 'historical'] as const;
export type CohortTier = (typeof COHORT_TIERS)[number];

export type Cohort = {
  id: string;
  /** Big banner name, e.g. `PAYPAL MAFIA`. */
  name: string;
  /** Subtitle, e.g. `2002` or `IPHONE TEAM · 2007`. */
  era: string;
  tier: CohortTier;
  /** Accent colour for the banner. */
  accent: string;
  members: PersonId[];
};

/** A filled roster slot. */
export type Pick = {
  role: Role;
  personId: PersonId;
  /** Which cohort they were drafted out of — useful for share cards later. */
  cohortId: string;
  /** Draft order, 1-6. */
  round: number;
};

export type Roster = Partial<Record<Role, Pick>>;

export type OutcomeTier =
  | 'imploded'
  | 'dead'
  | 'acquired'
  | 'unicorn'
  | 'decacorn'
  | 'ipo'
  | 'giant'
  | 'category'
  | 'trillion';

export type Result = {
  scores: Stats;
  /** 0-100 composite used to pick the outcome tier. */
  composite: number;
  tier: OutcomeTier;
  /** Headline outcome, e.g. `Acquired by Google`. */
  headline: string;
  /** Valuation in dollars. */
  valuation: number;
  /** Two-line funny verdict. */
  summary: string[];
};
